/**
 * The point-system beat's stage timeline (specs/champions-league, Story 13).
 *
 * The World Cup teaches its ladder as a zig-zag: a centre track, a dot per stage, cards
 * alternating left and right. That is the device that makes "the later rounds are worth more"
 * land before a single number is read, and the generic competition intro shipped without it —
 * it borrowed the WC's shell and its stagger but drew a flat three-column table.
 *
 * The WC's own layout is `WELCOME_POINTS_TIMELINE_LAYOUT`, a hardcoded list of exactly seven
 * entries whose module **throws at import time** if the count does not match the WC's points
 * table. Importing it would couple every competition to the World Cup's stage count, so the
 * layout is derived here instead, from whatever rows the competition actually has.
 *
 * Pure — no React, no theme, no network.
 */

import type { CompetitionIntroPointsRow } from '../types/competitionIntro.types';

export type CompetitionPointsTimelineIconId = 'flag' | 'swords' | 'medal' | 'trophy';

export interface CompetitionPointsTimelineEntry extends CompetitionIntroPointsRow {
  /** Stable key. The stage label, which is unique within a competition's stage order. */
  id: string;
  align: 'left' | 'right';
  icon: CompetitionPointsTimelineIconId;
  /** Points on one line rather than two, to keep a long ladder on one screen. */
  compact: boolean;
  /** This stage grants a x2, so its card says so (AC-13.4). */
  goldenBall: boolean;
}

export interface CompetitionPointsTimeline {
  entries: CompetitionPointsTimelineEntry[];
  /** A lone stage gets no track: there is nothing for a line to connect (AC-13.5). */
  showTrack: boolean;
}

/** Below this many stages the ladder fits without squeezing the closing rounds. */
const COMPACT_FROM_ROW_COUNT = 5;
/** How many closing rounds go compact once the ladder is long enough to need it. */
const COMPACT_TAIL = 2;

/**
 * Icons follow the shape of a tournament rather than its names: the opening rounds are a
 * campaign (`flag`), the middle is a bracket (`swords`), and the last two are the podium
 * (`medal` then `trophy`). A one-stage competition is a campaign, not a final.
 */
function iconForIndex(index: number, total: number): CompetitionPointsTimelineIconId {
  if (total === 1) return 'flag';
  if (index === total - 1) return 'trophy';
  if (index === total - 2) return 'medal';
  // Everything before the podium splits into an opening third and a knockout remainder.
  const openingCount = Math.max(1, Math.ceil((total - 2) / 2));
  return index < openingCount ? 'flag' : 'swords';
}

/**
 * @param rows  the ladder, already in the competition's own stage order and vocabulary
 * @param goldenBallStageLabels  labels of the stages that grant a x2, in the same vocabulary
 */
export function buildPointsTimeline(
  rows: readonly CompetitionIntroPointsRow[],
  goldenBallStageLabels: readonly string[] = [],
): CompetitionPointsTimeline {
  const boosted = new Set(goldenBallStageLabels);
  const total = rows.length;

  const entries = rows.map((row, index) => {
    const goldenBall = boosted.has(row.stage);
    return {
      ...row,
      id: row.stage,
      align: index % 2 === 0 ? ('left' as const) : ('right' as const),
      icon: iconForIndex(index, total),
      // A boosted stage never goes compact: the x2 line is the thing compact mode drops,
      // and it is the one line on that card a player most needs to see.
      compact: !goldenBall && total >= COMPACT_FROM_ROW_COUNT && index >= total - COMPACT_TAIL,
      goldenBall,
    };
  });

  return { entries, showTrack: total > 1 };
}
